import { Injectable } from '@angular/core';
import { UserClient } from './api/clients/user.client';
import { environment } from '@app-environments/environment';
import { UserContract } from '../types/api/user';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from './local-storage.service';
import {
  ListOfPossibleLanguages,
  STORAGE_NAMES,
  USER_ROLE,
} from '../shared/constants/constants';
import { MailFolderClient } from './api/clients/mail-folder.client';
import { UserMailAccountClient } from './api/clients/user-mail-account.client';
import { UserMailAccountContract } from '../types/api/user-mail-account';
import { OrganizationContract } from '../types/api/organization';
import { UserOrganizationClient } from './api/clients/user-organization.client';
import { ApiRequest } from './api/api.request';
import { MailAccountClient } from './api/clients/mail-account.client';
import { LoggingHandler } from './errors/logging.service';
import { MailAccountGetBadgeResponse } from '../types/api/mail-account';
import {
  MailAccountGetUnreadCountRequest,
  MailFolderGetUnreadCountRequest,
  MailFolderUnreadCount,
  UserFolderContract,
} from '../types/api/mail-folder';
import { SpaceInfo, StorageOrganisation } from '../types/common';
import { SpaceUsedPaymentPlanDetailTypes } from '../shared/constants/space-used';
import { getFolderByType } from '@app-utils/folders';
import { MailFolderType } from '@app-types/enums/mail-folder.type';
import { encodeInfoMailAccount } from '@app-utils/search-params';
import { UserSignInOrganizationType } from '@app-types/enums/sign-in-organization';
import { PermissionType } from '@app-types/enums/permission-type';
import { PermissionService } from '@app-services/permission/permission.service';
import { ROUTES } from '@app-shared/constants/routes/fe.routes';
import { EmailsPageStateService } from '@app-services/emails/emails-page-state.service';
import { Counts, TransformEmailListItemContract } from '@app-types/api/email';
import { EMAILS_TABS } from '@app-shared/constants/emails';
import { EmailTab } from '@app-types/enums/email-tab';
import { PermissionNavTabHelper } from '@app-services/permission/permission-nav-tab-helper';

const { USER_LOCALE, SELECTED_ORGANISATION } = STORAGE_NAMES;
const { ORGANIZATION, GENERAL, MAIL_ACCOUNTS, ORGANIZATIONS } = ROUTES;
const { RTA, USER, ROOT } = USER_ROLE;

@Injectable({
  providedIn: 'root',
})
export class UserWorkspaceService {
  public user: UserContract;
  public userFolders: UserFolderContract[] = [];
  public infoArchiveFolder: { [key: number]: { [key: number]: boolean } } = [];
  public mailAccounts: UserMailAccountContract[] = [];
  public mailAccountsError = '';
  public userOrganisaions: OrganizationContract[] = [];
  public currentOrganization: OrganizationContract | undefined;
  public spaceInfo: SpaceInfo = {
    maxAvailableSpaceUsed: 0,
    spaceUsed: 0,
    percentage: 0,
    alertPercentage: 90,
  };
  public mailAccountBadges: MailAccountGetBadgeResponse[] = [];
  folderCounts: { [key: string]: MailFolderUnreadCount[] } = {};
  public hideNotifications = true;
  isChangeAccount = false;
  public settingRoute: string | null = null;
  public userRole: string;

  mailAccId?: number;
  folderId?: number;

  constructor(
    public translate: TranslateService,
    private userClient: UserClient,
    private localStorageService: LocalStorageService,
    private mailFolderClient: MailFolderClient,
    private userMailAccountClient: UserMailAccountClient,
    private userOrganizationClient: UserOrganizationClient,
    private apiRequest: ApiRequest,
    private mailAccountClient: MailAccountClient,
    protected loggingHandler: LoggingHandler,
    private permissionService: PermissionService,
    protected emailsPageStateService: EmailsPageStateService,
    private permissionNavTabHelper: PermissionNavTabHelper
  ) {}

  async getUserRole(): Promise<void> {
    const hasPermissionOver = await this.permissionService.hasPermissionOver(
      null,
      null,
      PermissionType.CanEverything
    );

    if (!this.currentOrganization) {
      if (hasPermissionOver) {
        this.userRole = ROOT;
        this.settingRoute = `/${ORGANIZATIONS}`;
        return;
      }
    }

    const canManageOrganization =
      await this.permissionService.hasPermissionOver(
        this.currentOrganization?.organizationId ?? null,
        null,
        PermissionType.CanManageOrganization
      );

    if (canManageOrganization) {
      this.userRole = RTA;
      this.settingRoute = `/${ORGANIZATION}/${GENERAL}`;
      return;
    }

    this.userRole = USER;
    const organisationPage = this.currentOrganization?.organizationId
      ? await this.permissionNavTabHelper.getOrganizationPageTabs(
          this.currentOrganization.organizationId
        )
      : [];
    this.settingRoute = organisationPage.length
      ? organisationPage[0].route
      : null;
  }

  async getUserData(): Promise<void> {
    const response = await this.userClient.getUser();
    this.user = response.result;
    this.setLanguage(this.user?.localizationCode ?? '');
    this.translate.use(this.localStorageService.getData(USER_LOCALE));
  }

  private setLanguage(code: string): void {
    if (code) {
      this.localStorageService.setData(USER_LOCALE, code);
    } else {
      const elem = navigator.language.split('-')[0];
      if (ListOfPossibleLanguages.includes(elem)) {
        this.localStorageService.setData(USER_LOCALE, elem);
      } else {
        this.localStorageService.setData(USER_LOCALE, environment.language);
      }
    }
  }

  async loadFolders(): Promise<void> {
    try {
      const response = await this.mailFolderClient.getUserFolders();
      this.userFolders = response.data;
      const needFolderType = [
        MailFolderType.View,
        MailFolderType.Trash,
        MailFolderType.Spam,
      ];
      this.infoArchiveFolder = response.data.reduce(
        (acc, el) => {
          acc[el.mailAccountId] = {
            ...(acc?.[el.mailAccountId] ?? {}),
            [el.mailFolderId]:
              !!el?.organizationArchive &&
              !!el?.mailAccountArchive &&
              (!!el?.mailFolderArchive ||
                needFolderType.includes(el.folderType)),
          };
          return acc;
        },
        {} as { [key: number]: { [key: number]: boolean } }
      );
    } catch (error) {
      console.log(error);
      this.userFolders = [];
    }
  }

  async getMailAccounts(): Promise<void> {
    try {
      const response = await this.userMailAccountClient.getLoggedUserAccounts();
      this.mailAccounts = response.userMailAccounts.map(el => {
        if (el) {
          const folder = getFolderByType(
            this.userFolders,
            el.mailAccountId,
            MailFolderType.Inbox
          );
          const firstLink = folder
            ? encodeInfoMailAccount(
                el.mailAccountId,
                folder,
                MailFolderType.Inbox
              )
            : '';

          const firstLinkNotification = folder
            ? encodeInfoMailAccount(
                el.mailAccountId,
                folder,
                MailFolderType.Inbox,
                {
                  hideNotifications: false,
                }
              )
            : '';

          el = {
            ...el,
            firstFolder: folder,
            firstLink,
            firstLinkNotification,
          };
        }
        return el;
      });
    } catch (e: any) {
      this.mailAccountsError = `Code:${e.error?.Code ?? ''}, description:${e.error?.description ?? ''}`;
    }
  }

  async getUserOrganizations(): Promise<void> {
    this.userOrganisaions = (
      await this.userOrganizationClient.getUserOrganizations()
    ).organizations;

    const storedOrg: StorageOrganisation = this.localStorageService.getData(
      SELECTED_ORGANISATION
    );

    const targetOrgId =
      storedOrg?.organizationId ??
      (this.user.signInOrganizationType ===
      UserSignInOrganizationType.LastOrganization
        ? this.user.lastOrganizationId
        : this.user.preferredOrganizationId);

    const organisation = this.userOrganisaions?.find(
      org => org.organizationId === targetOrgId
    );
    this.currentOrganization = organisation
      ? organisation
      : this.userOrganisaions?.[0];
    if (this.currentOrganization) {
      this.apiRequest.setOrganisationId(
        this.currentOrganization.organizationId
      );

      const space = this.currentOrganization.spaceUsedPaymentPlanType
        ? SpaceUsedPaymentPlanDetailTypes[
            this.currentOrganization.spaceUsedPaymentPlanType
          ].value
        : 0;
      this.setSpaceInfo(space, this.currentOrganization?.spaceUsed ?? 0);
      this.spaceInfo.alertPercentage =
        this.currentOrganization.alertLevelSpaceUsed;
    }
  }

  private setSpaceInfo(space: number, spaceUsed: number): void {
    this.spaceInfo.maxAvailableSpaceUsed = space;
    this.spaceInfo.spaceUsed = spaceUsed ? spaceUsed : 0;
    this.spaceInfo.percentage =
      this.spaceInfo.maxAvailableSpaceUsed > 0
        ? +(
            (this.spaceInfo.spaceUsed / this.spaceInfo.maxAvailableSpaceUsed) *
            100
          ).toFixed(2)
        : 0;
  }

  public async getBadgesForMailAccounts(
    mailAccountIds: number[]
  ): Promise<void> {
    if (mailAccountIds.length) {
      const badgesForMailAccount =
        await this.mailAccountClient.getMailAccountsBadges({
          mailAccountIds,
          hideNotification: this.hideNotifications,
        });
      badgesForMailAccount.data.forEach(elem => {
        this.mailAccounts.map(x => {
          if (x.mailAccountId === elem.mailAccountId) {
            x.spaceUsed = elem.spaceUsed;
          }
        });
      });

      badgesForMailAccount.data.forEach(e =>
        this.mailAccountBadges.some(x => x.mailAccountId === e.mailAccountId)
          ? (this.mailAccountBadges = this.mailAccountBadges.map(x =>
              x.mailAccountId === e.mailAccountId ? e : x
            ))
          : this.mailAccountBadges.push(e)
      );
      const mailAccountBadgesNeedsToBeRemoved = mailAccountIds.filter(
        e => !badgesForMailAccount.data.some(x => x.mailAccountId === e)
      );
      if (mailAccountBadgesNeedsToBeRemoved.length) {
        this.mailAccountBadges = this.mailAccountBadges.filter(
          e => !mailAccountBadgesNeedsToBeRemoved.includes(e.mailAccountId)
        );
      }
    }
  }

  async refreshUnread({
    mailAccountId,
    updateBadges = true,
  }: {
    mailAccountId: number;
    updateBadges?: boolean;
  }): Promise<void> {
    if (updateBadges) {
      await this.getBadgesForMailAccounts([mailAccountId]);
    }
    const counts = await this.mailFolderClient.getUnreadCount(
      new MailAccountGetUnreadCountRequest(
        mailAccountId,
        this.hideNotifications
      )
    );
    this.folderCounts = { ...this.folderCounts, [mailAccountId]: counts?.data };
  }

  updateUnreadList(
    ids: number[],
    seen: boolean,
    email?: TransformEmailListItemContract
  ): void {
    const selectedEmails = email
      ? [email]
      : this.emailsPageStateService.dataSource.data.filter(el =>
          ids.includes(el.emailId)
        );

    selectedEmails.forEach(el => {
      if (el.seen === seen) return;
      const typeFolder = this.userFolders.find(
        folder =>
          el.mailAccountId === folder.mailAccountId &&
          el.mailFolderId === folder.mailFolderId
      )?.folderType;

      const isFolderIsInboxOrSend =
        typeFolder === MailFolderType.Inbox ||
        typeFolder === MailFolderType.Sent;

      this.updateUnread(
        el.mailFolderId,
        el.mailAccountId,
        !seen,
        1,
        isFolderIsInboxOrSend,
        typeFolder === MailFolderType.Inbox
      );
      if (el?.views?.length) {
        this.updateViewPage(el, !seen);
      }
    });
  }

  updateMoveList(ids: number[], moveToFolder?: number): void {
    const selectedEmails = this.emailsPageStateService.dataSource.data.filter(
      el => ids.includes(el.emailId)
    );

    const typeToFolder = this.userFolders.find(
      el =>
        el.mailAccountId ===
          this.emailsPageStateService.infoOpenedMailAccount?.acc &&
        el.mailFolderId === moveToFolder
    )?.folderType;

    const toFolderIsInboxOrSend =
      typeToFolder === MailFolderType.Inbox ||
      typeToFolder === MailFolderType.Sent;

    const toFolderIsDraftOrOutbox =
      typeToFolder === MailFolderType.Draft ||
      typeToFolder === MailFolderType.Outbox;

    selectedEmails.forEach(el => {
      const folderType = this.userFolders.find(
        folder =>
          folder.mailAccountId ===
            this.emailsPageStateService.infoOpenedMailAccount?.acc &&
          folder.mailFolderId === el.mailFolderId
      )?.folderType;

      const isInbox = folderType === MailFolderType.Inbox;
      const isSend = folderType === MailFolderType.Sent;
      const isDraftOrOutbox =
        folderType === MailFolderType.Draft ||
        folderType === MailFolderType.Outbox;
      const isNotSeen = !el.seen;

      const needRemove = isDraftOrOutbox || (!isDraftOrOutbox && isNotSeen);
      needRemove &&
        this.updateUnread(
          el.mailFolderId,
          el.mailAccountId,
          false,
          1,
          isInbox || isSend,
          isInbox
        );

      const needAdd =
        toFolderIsDraftOrOutbox || (!toFolderIsDraftOrOutbox && isNotSeen);
      needAdd &&
        moveToFolder &&
        this.updateUnread(
          moveToFolder,
          el.mailAccountId,
          true,
          1,
          toFolderIsInboxOrSend,
          false
        );

      if (el?.views?.length && isNotSeen) {
        this.updateViewPage(el);
      }
    });
  }

  updateViewPage(el: TransformEmailListItemContract, seen = false): void {
    const folderStarId = this.userFolders.find(
      folder =>
        folder.mailAccountId ===
          this.emailsPageStateService.infoOpenedMailAccount?.acc &&
        folder.folderType === MailFolderType.View
    )?.mailFolderId;

    if (
      this.emailsPageStateService.infoOpenedMailAccount?.folder ===
        folderStarId &&
      folderStarId
    ) {
      this.updateUnread(folderStarId, el.mailAccountId, seen, 1, false, false);
    }
  }

  updateUnreadMailAccBadges(
    mailAccountId: number,
    isPlus: boolean,
    count = 1
  ): void {
    this.mailAccountBadges = this.mailAccountBadges.map(el => {
      if (el.mailAccountId === mailAccountId) {
        const newCount = isPlus ? el.badge + count : el.badge - count;
        return { ...el, badge: newCount > 0 ? newCount : 0 };
      }
      return el;
    });
  }

  updateEmailTabBadges(tab: EmailTab, isPlus: boolean, count = 1): void {
    const emailTab = EMAILS_TABS[tab];
    const currCount =
      this.emailsPageStateService.counts?.[emailTab as keyof Counts] ?? 0;
    const countTab = isPlus ? currCount + count : currCount - count;
    this.emailsPageStateService.updateCounts({
      [emailTab]: countTab > 0 ? countTab : 0,
    });
  }

  updateUnread(
    folderId: number,
    mailAccountId: number,
    isPlus: boolean,
    count = 1,
    updateBadges = true,
    updateBadgesTab = true
  ): void {
    if (updateBadges) {
      this.updateUnreadMailAccBadges(mailAccountId, isPlus, count);
    }

    const mailAccountCounts = this.folderCounts[mailAccountId];
    const folderCounts = mailAccountCounts.find(
      el => el.mailFolderId === folderId
    );
    if (folderCounts) {
      const newCount = isPlus
        ? folderCounts.count + count
        : folderCounts.count - count;

      this.folderCounts = {
        ...this.folderCounts,
        [mailAccountId]:
          newCount > 0
            ? this.folderCounts[mailAccountId].map(el =>
                el.mailFolderId === folderId ? { ...el, count: newCount } : el
              )
            : this.folderCounts[mailAccountId].filter(
                el => el.mailFolderId !== folderId
              ),
      };
    } else {
      this.folderCounts = {
        ...this.folderCounts,
        [mailAccountId]: [
          ...this.folderCounts[mailAccountId],
          { mailFolderId: folderId, count },
        ],
      };
    }

    if (
      !updateBadgesTab ||
      this.emailsPageStateService.infoOpenedMailAccount.type !==
        MailFolderType.Inbox
    )
      return;
    this.updateEmailTabBadges(
      this.emailsPageStateService.infoOpenedMailAccount?.tab ??
        EmailTab.NewAndValidatedUnreadCount,
      isPlus,
      count
    );
  }

  public async refreshUnreadForFolder(
    folderId: number,
    mailAccountId: number,
    updateBadges = true
  ): Promise<any> {
    if (updateBadges && mailAccountId) {
      await this.getBadgesForMailAccounts([mailAccountId]);
    }
    const counts = await this.mailFolderClient.getFolderUnreadCount(
      new MailFolderGetUnreadCountRequest(folderId, this.hideNotifications)
    );
    if (counts === null) {
      this.folderCounts = {
        ...this.folderCounts,
        [mailAccountId]:
          this.folderCounts[mailAccountId]?.filter(
            e => e.mailFolderId !== folderId
          ) ?? [],
      };
    } else {
      const newData =
        this.folderCounts[mailAccountId]?.filter(
          e => e.mailFolderId !== counts.mailFolderId
        ) ?? [];
      newData.push(counts);
      this.folderCounts = {
        ...this.folderCounts,
        [mailAccountId]: newData,
      };
    }
  }

  setSettingsMaxSpace(value: number): void {
    this.setSpaceInfo(value, this.spaceInfo.spaceUsed);
  }

  setSettingsAlertLevel(value: number): void {
    this.spaceInfo.alertPercentage = value;
  }
}
